import { useState, useRef, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";

import {
    Stack,
    Typography,
    Table,
    TableBody,
    Button,
    Container,
    // Tooltip,
} from "@mui/material";

import EChartsReact from "echarts-for-react";

import NoDataRow from "../../NoDataRow";
import HoverPaper from "../HoverPaper";

import { tooltip, tooltipST } from "../../../helpers/eCharts";
import { fns } from "../../../helpers/common";
import Colours, { pastelColours } from "../../../helpers/colours";
import Icons from "../../../helpers/icons";
import { DISPLAY_ELEMENT_HELPER } from "../../../helpers/filters";

export const REPORT_CHART_CATEGORY_STACKED = "REPORT_CHART_CATEGORY_STACKED";
const ReportChartCategoryStacked = ({
    js,
    data,
    entriesLimit = 3,
    metric = "bookingsTotal",
    title = "",
}) => {
    const { id: hotelID } = useSelector((state) => state.hotelID);

    const [update, setUpdate] = useState(0);

    const transformKeyedData = (data, metric, entriesLimit = 5) => {
        const mapByDate = {};
        const offerTotals = {};

        Object.entries(data).forEach(([key, { keys, result }]) => {
            if (Object.keys(result).length > 0) {
                const { OfferTitle, Year, Month, Day } =
                    Object.fromEntries(keys);

                const dateKey = `${Year}-${Month}-${Day}`;
                const value = result?.[metric] ?? 0;

                if (!mapByDate[dateKey]) {
                    mapByDate[dateKey] = {};
                }

                mapByDate[dateKey][OfferTitle] = value;

                offerTotals[OfferTitle] =
                    (offerTotals[OfferTitle] || 0) + value;
            }
        });

        // Ordenar fechas
        const sortedDates = Object.keys(mapByDate).sort(
            (a, b) => new Date(a) - new Date(b),
        );

        const sortedOffers = Object.entries(offerTotals)
            .filter(([_, total]) => total > 0) // Delete offers without data
            .sort((a, b) => b[1] - a[1])
            .map(([offer]) => offer);

        const mainOffers = sortedOffers.slice(0, entriesLimit - 1);

        const otherOffers = sortedOffers.slice(entriesLimit - 1);

        const palette = pastelColours;
        const seriesData = mainOffers.map((offer, i) => ({
            name: offer,
            type: "bar",
            stack: "total",
            emphasis: { focus: "series" },
            itemStyle: { color: palette[i % palette.length] },
            data: sortedDates.map((dateKey) => mapByDate[dateKey][offer] ?? 0),
        }));

        if (otherOffers.length > 0) {
            seriesData.push({
                name: "Others",
                type: "bar",
                stack: "total",
                emphasis: { focus: "series" },
                itemStyle: { color: "#BDBDBD" },
                data: sortedDates.map((dateKey) =>
                    otherOffers.reduce(
                        (sum, offer) => sum + (mapByDate[dateKey][offer] ?? 0),
                        0,
                    ),
                ),
            });
        }

        const xAxisLabels = sortedDates.map((dateKey) =>
            new Date(dateKey).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }),
        );
        return { xAxisLabels, seriesData };
    };

    const { xAxisLabels, seriesData } = useMemo(() => {
        if (!data) return { xAxisLabels: [], seriesData: [] };
        return transformKeyedData(data, metric);
    }, [data, metric]);

    const options = useMemo(() => {
        return {
            tooltip: {
                ...tooltip,
                formatter: (params) => {
                    return tooltipST(params, {
                        formatter: (value) =>
                            fns(
                                hotelID,
                                value,
                                DISPLAY_ELEMENT_HELPER[metric].format ?? {},
                            ),
                        sort: "asc",
                    });
                },
            },
            legend: { show: false },
            xAxis: {
                type: "category",
                data: xAxisLabels,
            },
            yAxis: { type: "value" },
            series: seriesData,
        };
    }, [xAxisLabels, seriesData]);

    const [legend, setLegend] = useState({});
    const chartRef = useRef(null);

    useEffect(() => {
        if (!seriesData) return;

        const newLegend = {};

        seriesData.forEach((serie) => {
            newLegend[serie.name] = {
                show: true,
                color: serie.itemStyle?.color ?? serie.color ?? undefined,
            };
        });

        setLegend(newLegend);
    }, [seriesData]);

    const handleOnClickLegend = (cat) => {
        const echartInstance = chartRef.current.getEchartsInstance();
        let auxLegend = {
            ...legend,
        };
        auxLegend[cat].show = !legend[cat].show;

        setLegend(auxLegend);

        echartInstance.dispatchAction({
            type: "legendToggleSelect",
            name: cat,
        });
    };
    const handleOnMouseOverLegend = (cat) => {
        const echartInstance = chartRef.current.getEchartsInstance();
        echartInstance.dispatchAction({
            type: "highlight",
            seriesName: cat,
        });
    };
    const handleOnMouseOutLegend = (cat) => {
        const echartInstance = chartRef.current.getEchartsInstance();
        echartInstance.dispatchAction({
            type: "downplay",
            seriesName: cat,
        });
    };

    return (
        <>
            <Typography fontWeight={"bold"} sx={{ pt: 2 }}>
                {title}
            </Typography>

            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{ p: 2, backgroundColor: Colours.notificationCard }}
            >
                {seriesData.length > 0 ? (
                    <>
                        <EChartsReact
                            key={update}
                            ref={chartRef}
                            option={options}
                            style={{
                                width: "100%",
                                height: "500px",
                            }}
                        />
                        {js.groupBy.length === 4 && (
                            <Stack
                                direction="row"
                                justifyContent="center"
                                flexWrap={"wrap"}
                            >
                                {Object.keys(legend).length > 0 &&
                                    chartRef &&
                                    Object.keys(legend).map((cat, index) => {
                                        return (
                                            <Button
                                                key={index}
                                                id={`series-${cat}`}
                                                variant="outlined"
                                                sx={{
                                                    paddingY: 1,
                                                    paddingX: 0.5,
                                                    m: 1,
                                                    textTransform: "none",
                                                    color: "black",
                                                    border: "1px solid lightgrey",

                                                    opacity: () =>
                                                        legend[cat].show
                                                            ? 1
                                                            : 0.5,
                                                }}
                                                onClick={() =>
                                                    handleOnClickLegend(cat)
                                                }
                                                onMouseOver={() =>
                                                    handleOnMouseOverLegend(cat)
                                                }
                                                onMouseOut={() =>
                                                    handleOnMouseOutLegend(cat)
                                                }
                                            >
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    display="flex"
                                                >
                                                    <Icons.Square
                                                        sx={{
                                                            color: legend[cat]
                                                                .color,
                                                            marginRight: 0.5,
                                                            pointerEvents:
                                                                "none",
                                                            fontSize: "20px",
                                                        }}
                                                    />

                                                    <Typography fontSize="14px">
                                                        {cat}
                                                    </Typography>
                                                </Stack>
                                            </Button>
                                        );
                                    })}
                            </Stack>
                        )}
                    </>
                ) : (
                    <Table>
                        <TableBody>
                            <NoDataRow text="No rate plan data available for this selection" />
                        </TableBody>
                    </Table>
                )}
            </Container>
        </>
    );
};

export default ReportChartCategoryStacked;
